import { v4 as uuid } from 'uuid';

const CHUNK_SIZE = 1024 * 1024 * 5;
const CALC_ETA_NB_CHUNK = 5;

const wait = (delay) => new Promise((resolve) => setTimeout(resolve, delay));

const retry = async (operation, options = {}) => {
  const { retries, delay, onError = () => {} } = options;

  return operation().catch((err) => {
    onError(err);
    if (retries > 0) {
      return wait(delay).then(() => retry(operation, { ...options, retries: retries - 1 }));
    } else {
      throw err;
    }
  });
};

class UploadManager {
  constructor({ files, getSession }) {
    this.files = files.map((file) => ({ ...file, id: uuid() }));
    this.getSession = getSession;
    this.registeredEvents = [];
    this.uploading = false;
    this.chunkUploadSpeeds = [];
    this.wakeLock = null;
    document.addEventListener('visibilitychange', () => this.onVisibilityChange());
  }

  //UPLOAD

  addChunkSpeed(chunkUploadSpeed) {
    this.chunkUploadSpeeds.push(chunkUploadSpeed);
    if (this.chunkUploadSpeeds.length > CALC_ETA_NB_CHUNK) {
      this.chunkUploadSpeeds.shift();
    }
  }

  calcPercent(uploadInProgressPercent, sizeFileInProgress) {
    const [totalSize, sizeFilesAlreadyUploaded] = this.files.reduce(
      (acc, file) => {
        acc[0] += file.blob?.size || 0;
        if (file.isUploaded) {
          acc[1] += file.blob?.size || 0;
        }
        return acc;
      },
      [0, 0]
    );
    const sizeFileInProgressUploaded = ((sizeFileInProgress || 0) * uploadInProgressPercent) / 100;
    return {
      total: (100 * (sizeFileInProgressUploaded + sizeFilesAlreadyUploaded)) / totalSize,
      eta: this.chunkUploadSpeeds.length > 0 ? ((totalSize - (sizeFileInProgressUploaded + sizeFilesAlreadyUploaded)) / (this.chunkUploadSpeeds.reduce((a, speed) => a + speed, 0) / this.chunkUploadSpeeds.length)) : null,
    };
  }

  async uploadChunks(id, url, startOffset = 0) {
    let offset = startOffset;
    const file = this.files.find((file) => file.id === id)?.blob;

    if (!file) {
      return;
    }

    do {
      const chunkSize = file.size - offset >= CHUNK_SIZE ? CHUNK_SIZE : file.size - offset;
      const actualOffset = offset;
      let startTime = Date.now();
      const res = await retry(
        () =>
          fetch(url, {
            method: 'PUT',
            headers: {
              'Content-Length': chunkSize,
              'Content-Range': `bytes ${actualOffset}-${actualOffset + chunkSize - 1}/${file.size}`,
            },
            body: file.slice(actualOffset, actualOffset + chunkSize),
          }),
        {
          retries: Infinity,
          delay: 2000,
          onError: () => {
            this.addChunkSpeed(0);
            this.triggerEvent('progress', {
              id,
              actual: (offset * 100) / file.size,
              ...this.calcPercent((offset * 100) / file.size, file.size),
            });
            startTime = Date.now();
          }
        }
      );
      this.addChunkSpeed(chunkSize / ((Date.now() - startTime) / 1000));
      this.triggerEvent('progress', {
        id,
        actual: (offset * 100) / file.size,
        ...this.calcPercent((offset * 100) / file.size, file.size),
      });

      offset += CHUNK_SIZE;

      if (res.ok || !this.files.find((file) => file.id === id)) {
        break;
      }
    } while (offset < file.size);
  }

  async uploadNextFile() {
    const file = this.files.find(({ isUploaded }) => !isUploaded);
    if (file) {
      this.uploading = true;
      await this.requestWakeLock();
      this.triggerEvent('progress', {
        id: file.id,
        actual: 0,
        ...this.calcPercent(0, file.size),
      });
      // Resume upload where it left off to upload remaining chunks
      const session = await this.getSession(file);
      const range = await fetch(session, {
          method: 'PUT',
          headers: {
              'Content-Length': 0,
              'Content-Range': `bytes */${file.blob.size}`,
          },
      }).then((res) => res.headers.get('range'));
      const startOffset = range ? Number(range.split('-').pop()) + 1 : 0;
      await this.uploadChunks(file.id, session, startOffset);
      this.files = this.files.map((f) => (f.id === file.id ? { ...f, isUploaded: true } : f));
      if (this.files.find((f) => f.id === file.id)) {
        this.triggerEvent('fileUploaded', file);
      }
      this.uploading = false;
      await this.releaseWakeLock();
      this.uploadNextFile();
    }
  }

  addFiles(files) {
    const newFiles = files.map((file) => ({ ...file, id: uuid() }));
    this.files = [...this.files, ...newFiles];
    if (!this.uploading) {
      this.uploadNextFile();
    }
    return newFiles.map(({id}) => id);
  }

  deleteFile(idx) {
    this.files = this.files.filter((file, fileIdx) => fileIdx !== idx);
  }

  deleteAllFiles() {
    this.files = [];
  }

  getAllFiles() {
    return this.files;
  }

  getFilesUploaded() {
    return this.files.filter((file) => file.isUploaded);
  }

  getFilesToUpload() {
    return this.files.filter((file) => !file.isUploaded);
  }

  renameFile(idx, name) {
    this.files = this.files.map((file, fileIdx) => (fileIdx === idx ? { ...file, fileName: name } : file));
  }

  //WAKE LOCK

  async requestWakeLock() {
    try {
      if ('wakeLock' in navigator) {
        this.wakeLock = await navigator.wakeLock.request('screen');
      }
    } catch (e) {
      this.wakeLock = null;
    }
  }

  async releaseWakeLock() {
    if (this.wakeLock) {
      await this.wakeLock.release();
      this.wakeLock = null;
    }
  }

  async onVisibilityChange() {
    if (this.wakeLock !== null && document.visibilityState === 'visible') {
      this.wakeLock = await navigator.wakeLock.request('screen');
    }
  }

  //EVENTS

  addEventListener(type, callback) {
    this.registeredEvents.push({
      type,
      callback,
    });
  }

  removeEventListener(type, callback) {
    this.registeredEvents = this.registeredEvents.filter((event) => event.callback === callback && event.type === type);
  }

  triggerEvent(type, data) {
    this.registeredEvents.filter((event) => event.type === type).forEach((event) => event.callback(data));
  }
}

export default UploadManager;
